import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import {
  useNotifications,
  usePatientDiaryLayoutSettings,
  PATIENT_DIARY_LAYOUT_SIDEBAR,
  PATIENT_DIARY_LAYOUT_TAB,
  useDocumentTabViewSettings,
  DOCUMENT_TAB_VIEW_CODES,
  DOCUMENT_TAB_VIEW_ALL,
} from '@medprompt/shared';
import { existingCodesMatch } from './PatientDiaryPanel';

const TAB_SCROLL_SELECTOR = '.tab-content-wrapper, .sidebar-tab-pane-body, .sidebar-actions-pane, .patient-diary-content';

const resetTabScroll = (root) => {
  if (!root) return;
  root.scrollTop = 0;
  root.querySelectorAll(TAB_SCROLL_SELECTOR).forEach((el) => {
    el.scrollTop = 0;
  });
};

/** Sidebar tabs and layout, the patient diary panel, the comments panel, and diary previews. */
const useDocumentSidebar = ({ doc, location, documentService, updateDoc, isEditingEnabled, loadKey }) => {
  const { addNotification } = useNotifications();
  const { layout: patientDiaryLayout, setLayout: setPatientDiaryLayout } = usePatientDiaryLayoutSettings();
  const { tabViewMode, setTabViewMode } = useDocumentTabViewSettings();

  const [activeTab, setActiveTab] = useState('Details');
  const [isPatientDiaryVisible, setIsPatientDiaryVisible] = useState(false);
  const [isCommentsVisible, setIsCommentsVisible] = useState(false);
  const [detailsTabAnimating, setDetailsTabAnimating] = useState(false);
  const [highlightedExistingCodeId, setHighlightedExistingCodeId] = useState(null);
  const [showDocumentPreview, setShowDocumentPreview] = useState(false);
  const [previewDocumentId, setPreviewDocumentId] = useState(null);
  const existingCodesPaneRef = useRef(null);
  const tabContentWrapperRef = useRef(null);
  const sidebarLayoutRef = useRef(null);

  useLayoutEffect(() => {
    resetTabScroll(sidebarLayoutRef.current);
  }, [activeTab]);

  useEffect(() => {
    setShowDocumentPreview(false);
    setPreviewDocumentId(null);
  }, [loadKey]);

  // When arriving via auto-navigate after filing, pulse the Details tab and scroll to top
  useEffect(() => {
    if (!location.state?.autoShowDetailsTab) return;
    setDetailsTabAnimating(true);
    const timer = setTimeout(() => {
      setDetailsTabAnimating(false);
      setActiveTab('Details');
      if (tabContentWrapperRef.current) {
        tabContentWrapperRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [location.state]);

  useEffect(() => {
    if (!highlightedExistingCodeId) {
      return undefined;
    }
    const pane = existingCodesPaneRef.current;
    const row = pane?.querySelector(
      `[data-existing-code-id="${CSS.escape(String(highlightedExistingCodeId))}"]`
    );
    if (row && pane) {
      const paneRect = pane.getBoundingClientRect();
      const rowRect = row.getBoundingClientRect();
      if (paneRect.height > 0 && rowRect.height > 0) {
        const outOfView = rowRect.top < paneRect.top + 8 || rowRect.bottom > paneRect.bottom - 8;
        if (outOfView) {
          const delta = rowRect.top - paneRect.top;
          pane.scrollTo({ top: Math.max(0, pane.scrollTop + delta - 12), behavior: 'smooth' });
        }
      }
    }
    const timer = setTimeout(() => setHighlightedExistingCodeId(null), 1800);
    return () => clearTimeout(timer);
  }, [highlightedExistingCodeId, activeTab, tabViewMode]);

  const handlePatientDiaryLayoutChange = useCallback((newLayout) => {
    setPatientDiaryLayout(newLayout);
    if (newLayout === PATIENT_DIARY_LAYOUT_SIDEBAR) {
      setIsPatientDiaryVisible(true);
      setActiveTab(prev => (prev === 'PatientDiary' ? 'Details' : prev));
    } else if (newLayout === PATIENT_DIARY_LAYOUT_TAB) {
      setIsPatientDiaryVisible(false);
      setActiveTab('PatientDiary');
    }
  }, [setPatientDiaryLayout]);

  const handleTabViewModeChange = useCallback((nextMode) => {
    setTabViewMode(nextMode);
    setActiveTab((current) => {
      if (nextMode === DOCUMENT_TAB_VIEW_ALL) {
        return current === 'Actions' ? 'Actions' : 'Content';
      }
      if (current === 'Content') {
        return nextMode === DOCUMENT_TAB_VIEW_CODES ? 'Codes' : 'Details';
      }
      if (nextMode === DOCUMENT_TAB_VIEW_CODES && (current === 'ExistingCodes' || current === 'NewCodes')) {
        return 'Codes';
      }
      if (nextMode !== DOCUMENT_TAB_VIEW_CODES && current === 'Codes') {
        return 'ExistingCodes';
      }
      return current;
    });
  }, [setTabViewMode]);

  const handleDiaryExistingCodeClick = useCallback(async (existingCode, category) => {
    if (!isEditingEnabled() || !doc?.documentId || !existingCode?.code?.code) return;

    const listKey = category === 'Active' ? 'activeCodes' : 'significantPastCodes';
    const existing = doc.existingPatientCodes || { activeCodes: [], significantPastCodes: [] };
    const list = existing[listKey] || [];
    const match = list.find((code) => existingCodesMatch(code, existingCode));

    if (match) {
      const newSelected = !match.isSelected;
      const updatedList = list.map((code) =>
        existingCodesMatch(code, match) ? { ...code, isSelected: newSelected } : code
      );
      updateDoc({
        ...doc,
        existingPatientCodes: {
          ...existing,
          [listKey]: updatedList,
        },
      });
      documentService.updateDocumentExistingCode(doc.documentId, match.codeId, {
        isSelected: newSelected ? true : null,
        isDeselected: !newSelected ? true : null,
      });
      return;
    }

    const codeToAdd = {
      activeCodes: [],
      significantPastCodes: [],
    };
    if (category === 'Active') {
      codeToAdd.activeCodes.push(existingCode);
    } else {
      codeToAdd.significantPastCodes.push(existingCode);
    }

    updateDoc({
      ...doc,
      existingPatientCodes: {
        ...existing,
        activeCodes: existing.activeCodes || [],
        significantPastCodes: existing.significantPastCodes || [],
        [listKey]: [...list, { ...existingCode, isSelected: true }],
      },
    });

    if (tabViewMode === DOCUMENT_TAB_VIEW_CODES) {
      setActiveTab('Codes');
      if (existingCode.codeId) {
        setHighlightedExistingCodeId(existingCode.codeId);
      }
    } else if (tabViewMode === DOCUMENT_TAB_VIEW_ALL) {
      setActiveTab('Content');
      if (existingCode.codeId) {
        setHighlightedExistingCodeId(existingCode.codeId);
      }
    } else if (patientDiaryLayout === PATIENT_DIARY_LAYOUT_SIDEBAR) {
      setActiveTab('ExistingCodes');
    }

    try {
      await documentService.addExistingPatientCodes(doc.documentId, codeToAdd);
      await documentService.updateDocumentExistingCode(doc.documentId, existingCode.codeId, {
        isSelected: true,
        isDeselected: null,
      });
    } catch (error) {
      console.error('Error adding existing patient code from diary:', error);
      addNotification(`diary-code-add-error-${doc.documentId}`, 'Failed to add code', { isError: true });
    }
  }, [isEditingEnabled, doc, documentService, updateDoc, addNotification, patientDiaryLayout, tabViewMode]);

  const toggleCommentsVisible = useCallback(() => setIsCommentsVisible(visible => !visible), []);
  const closeComments = useCallback(() => setIsCommentsVisible(false), []);
  const togglePatientDiaryVisible = useCallback(() => setIsPatientDiaryVisible(visible => !visible), []);

  const openDocumentPreview = useCallback((selectedDocumentId) => {
    setPreviewDocumentId(selectedDocumentId);
    setShowDocumentPreview(true);
  }, []);

  const closeDocumentPreview = useCallback(() => {
    setShowDocumentPreview(false);
    setPreviewDocumentId(null);
  }, []);

  return {
    activeTab,
    setActiveTab,
    tabViewMode,
    handleTabViewModeChange,
    patientDiaryLayout,
    handlePatientDiaryLayoutChange,
    isPatientDiaryVisible,
    togglePatientDiaryVisible,
    isCommentsVisible,
    toggleCommentsVisible,
    closeComments,
    detailsTabAnimating,
    highlightedExistingCodeId,
    existingCodesPaneRef,
    tabContentWrapperRef,
    sidebarLayoutRef,
    handleDiaryExistingCodeClick,
    showDocumentPreview,
    previewDocumentId,
    openDocumentPreview,
    closeDocumentPreview,
  };
};

export default useDocumentSidebar;
